import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	Container,
	FormControlLabel,
	Grid,
	LinearProgress,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Snackbar,
	Typography,
	Checkbox,
	CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { motion, AnimatePresence } from "framer-motion";
import { CRYPTO_TOKEN_SECRET_KEY } from "../../config";
import { verifyTermsAndConditionsAction } from "../../Actions/UserActions";
import { listTermsAndConditionsAction } from "../../Actions/termsAndConditionsAction";
import { getUserData } from "../../Actions/common";
import {
	MyArticleOutlinedIcon,
	MyCheckIcon,
	MyCloseIcon,
	MyShieldIcon,
} from "../../Assets/svg/MyIcons";
import { useUser } from "../../components/ContextAPI/ProfileContext";
import FooterComponent from "../../components/Common/Footer";
import logo from "../../Assets/icon.png";

// Floating Logo Animation Component
const FloatingLogo = ({ delay, size, x, y, logoSrc }) => (
	<motion.div
		className="position-absolute"
		style={{ width: size, height: size, left: x, top: y }}
		animate={{
			y: [0, -40, 0, 40, 0],
			x: [0, 30, 0, -30, 0],
			rotate: [0, 8, 0, -8, 0],
			scale: [1, 1.05, 1, 0.95, 1],
		}}
		transition={{
			duration: 20 + delay * 3,
			delay,
			repeat: Infinity,
			ease: "easeInOut",
		}}
	>
		<img
			src={logoSrc}
			alt="Qbot Logo"
			className="w-100 h-100 object-fit-contain opacity-25"
		/>
	</motion.div>
);

// Gradient Mesh Background
const GradientMesh = () => (
	<motion.div
		className="position-absolute w-100 h-100 gradient-mesh-bg"
		animate={{
			opacity: [0.5, 0.8, 0.5],
		}}
		transition={{
			duration: 10,
			repeat: Infinity,
			ease: "easeInOut",
		}}
	/>
);

const TnCSection = () => {
	const navigate = useNavigate();
	const { updateUserData } = useUser();
	const userData = localStorage.getItem("userData") ? getUserData() : null;

	const [terms, setTerms] = useState([]);
	const [selectedTerm, setSelectedTerm] = useState(null);
	const [checkedTerms, setCheckedTerms] = useState(new Set());
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	// Floating logos configuration
	const floatingLogos = [
		{ delay: 0, size: 250, x: "3%", y: "calc(100% - 375px)" },
		{ delay: 3, size: 200, x: "calc(100% - 240px)", y: "100px" },
	];

	useEffect(() => {
		const fetchTerms = async () => {
			try {
				setLoading(true);
				const response = await listTermsAndConditionsAction({
					forUser: "true",
				});
				if (!response.data || response.data.length === 0) {
					setTimeout(() => {
						navigate("/dashboard");
					}, 500);
					return;
				}
				setTerms(response.data);
				setSelectedTerm(response.data[0]);
			} catch (error) {
				console.error("Error fetching terms:", error);
				setSnackbar({
					open: true,
					message:
						error?.response?.data?.data?.message ||
						"Failed to fetch terms",
					severity: "error",
				});
				setTimeout(() => {
					navigate("/dashboard");
				}, 2000);
			} finally {
				setLoading(false);
			}
		};
		fetchTerms();
	}, [navigate]);

	const handleTermToggle = (termId) => {
		const newChecked = new Set(checkedTerms);
		if (newChecked.has(termId)) {
			newChecked.delete(termId);
		} else {
			newChecked.add(termId);
		}
		setCheckedTerms(newChecked);
	};

	const requiredTerms = terms.filter((t) => t.isRequired);
	const checkedRequiredCount = requiredTerms.filter((t) =>
		checkedTerms.has(t.id),
	).length;
	const progressPercentage =
		requiredTerms.length > 0
			? (checkedRequiredCount / requiredTerms.length) * 100
			: 0;

	const allRequiredChecked = requiredTerms.every((t) =>
		checkedTerms.has(t.id),
	);

	const handleAccept = async () => {
		try {
			setSubmitting(true);
			const acceptedTermIds = terms.map((t) => t.id);
			await verifyTermsAndConditionsAction({ acceptedTermIds });
			setSnackbar({
				open: true,
				message: "Terms accepted successfully.",
				severity: "success",
			});

			const encryptedData = localStorage.getItem("userData");
			if (encryptedData) {
				// Decrypt
				const bytes = CryptoJS.AES.decrypt(
					encryptedData,
					CRYPTO_TOKEN_SECRET_KEY,
				);
				const decryptedData = JSON.parse(
					bytes.toString(CryptoJS.enc.Utf8),
				);

				// Update
				decryptedData.isTermsRead = true;

				// Re-encrypt and save
				const encryptedUserData = CryptoJS.AES.encrypt(
					JSON.stringify(decryptedData),
					CRYPTO_TOKEN_SECRET_KEY,
				).toString();
				localStorage.setItem("userData", encryptedUserData);

				// Update context
				updateUserData(decryptedData);
			}

			setTimeout(() => {
				navigate("/dashboard");
			}, 1000);
		} catch (error) {
			console.error("Error accepting terms:", error);
			setSnackbar({
				open: true,
				message:
					error?.response?.data?.message || "Failed to accept terms",
				severity: "error",
			});
		} finally {
			setSubmitting(false);
		}
	};
	const handleDecline = () => {
		localStorage.removeItem("token");
		localStorage.removeItem("GLOBAL_SETTING");
		localStorage.removeItem("userData");
		localStorage.removeItem(`${userData?.id}_permissionData`);

		setSnackbar({
			open: true,
			message: "Logout successfully.",
			severity: "success",
		});

		setTimeout(() => {
			navigate("/login");
		}, 1000);
	};

	if (loading) {
		return (
			<div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light bg-gradient">
				<CircularProgress size={60} />
				<Typography variant="h5" className="mt-4">
					Loading Terms & Conditions...
				</Typography>
			</div>
		);
	}

	if (!terms || terms.length === 0) {
		return (
			<div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light bg-gradient">
				<MyCheckIcon
					style={{ fontSize: 80, color: "#4caf50" }}
					className="mb-3"
				/>
				<Typography variant="h4">
					All terms already accepted!
				</Typography>
				<Typography variant="body1" className="mt-2 text-gray-600">
					Redirecting to dashboard...
				</Typography>
			</div>
		);
	}

	return (
		<Box
			className="min-vh-100 position-relative overflow-auto"
			style={{
				background:
					"linear-gradient(135deg, rgba(27, 46, 112, 0.05) 0%, rgba(230, 238, 255, 0.4) 100%)",
			}}
		>
			{/* Animated Background Elements */}
			<div
				className="position-fixed w-100 h-100 overflow-hidden"
				style={{ pointerEvents: "none" }}
			>
				<GradientMesh />
				<div className="position-absolute w-100 h-100 opacity-25 bg-grid-pattern" />
			</div>

			{/* Floating Logos */}
			<div
				style={{
					position: "fixed",
					inset: 0,
					overflow: "hidden",
					pointerEvents: "none",
					zIndex: 0,
				}}
			>
				{floatingLogos.map((logoConfig, i) => (
					<FloatingLogo
						key={`logo-${i}`}
						{...logoConfig}
						logoSrc={logo}
					/>
				))}
			</div>

			<div className="position-relative z-1 min-vh-100 d-flex flex-column">
				{/* Header */}
				<motion.header
					className="p-4 bg-opacity-75"
					style={{ flexShrink: 0 }}
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
				>
					<Container maxWidth="lg">
						<div className="d-flex justify-content-between align-items-center">
							<div className="d-flex align-items-center">
								<motion.div
									className="rounded-3 overflow-hidden"
									style={{ height: 48, width: 48 }}
									whileHover={{ scale: 1.05, rotate: 5 }}
									transition={{
										type: "spring",
										stiffness: 400,
									}}
								>
									<img
										src={logo}
										alt="Qbot"
										className="w-100 h-100"
										style={{ objectFit: "contain" }}
									/>
								</motion.div>
								<div className="ms-3">
									<Typography
										variant="h5"
										className="fw-bold"
									>
										Qbot
									</Typography>
									<Typography
										variant="body1"
										className="text-gray-600"
									>
										Legal Agreement
									</Typography>
								</div>
							</div>
							<motion.div
								className="d-none d-sm-flex align-items-center"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ delay: 0.3 }}
							>
								<MyShieldIcon
									style={{ fontSize: 16 }}
									className="me-2"
								/>
								<Typography
									variant="h6"
									className="text-gray-700"
								>
									Secure & Encrypted
								</Typography>
							</motion.div>
						</div>
					</Container>
				</motion.header>

				{/* Main Content */}
				<main className="flex-fill py-4 px-3">
					<Container maxWidth="lg" className="d-flex flex-column">
						{/* Title Section */}
						<motion.div
							className="text-center mb-2"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.1 }}
						>
							<motion.div
								className="d-inline-flex align-items-center mb-2 px-3 py-1 rounded-pill bg-primary bg-opacity-10 border border-primary border-opacity-25 gap-2"
								whileHover={{ scale: 1.02 }}
							>
								<MyShieldIcon style={{ fontSize: 16 }} />
								<Typography
									variant="body1"
									style={{ fontWeight: 500 }}
								>
									Review Required
								</Typography>
							</motion.div>
							<Typography variant="h3" className="fw-bold mb-2">
								Terms & Conditions
							</Typography>
							<Typography
								variant="body1"
								className="mx-auto w-100 text-gray-700"
								style={{ maxWidth: 600 }}
							>
								To continue using the Qbot Portal, you must read
								and accept our Terms & Conditions. These terms
								outline your responsibilities and our policies
								regarding data privacy and usage of the system.
							</Typography>
						</motion.div>

						{/* Progress Bar */}
						{requiredTerms.length > 0 && (
							<motion.div
								className="mx-auto mb-6"
								style={{
									maxWidth: 600,
									width: "100%",
								}}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.2 }}
							>
								<div className="d-flex justify-content-between mb-2">
									<Typography
										variant="body2"
										className="text-gray-600"
									>
										Progress
									</Typography>
									<Typography
										variant="body2"
										className="fw-semibold"
									>
										{checkedRequiredCount} /{" "}
										{requiredTerms.length} required
									</Typography>
								</div>
								<LinearProgress
									variant="determinate"
									value={progressPercentage}
									sx={{
										height: 8,
										borderRadius: 4,
										backgroundColor: "#e0e0e0",
										"& .MuiLinearProgress-bar": {
											borderRadius: 4,
											background: "var(--bs-primary)",
										},
									}}
								/>
							</motion.div>
						)}

						{/* Two Column Grid - FIXED WITH MUI GRID */}
						<Grid container spacing={2} className="min-vh-50">
							{/* Left Panel - 25% */}
							<Grid item xs={12} lg={4}>
								<motion.div
									initial={{ opacity: 0, x: -30 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.5, delay: 0.3 }}
									className="h-100"
								>
									<Card
										className="bg-white bg-opacity-95 shadow-sm overflow-hidden d-flex flex-column min-vh-50"
										sx={{
											minHeight: 515,
											maxHeight: 515,
											height: 515,
											display: 'flex',
											flexDirection: 'column',
										}}
									>
										<CardHeader
											title={
												<div className="d-flex align-items-center">
													<motion.div
														className="d-flex align-items-center justify-content-center me-3"
														style={{
															height: 40,
															width: 40,
															borderRadius: 8,
															background: "rgba(25, 118, 210, 0.1)",
														}}
														whileHover={{
															rotate: 10,
														}}
													>
														<MyArticleOutlinedIcon
															style={{
																fontSize: 20,
															}}
														/>
													</motion.div>
													<div>
														<Typography
															variant="h5"
															className="fw-semibold"
														>
															Documents
														</Typography>
														<Typography
															variant="body2"
															className="text-gray-600"
														>
															{terms.length}{" "}
															agreements
														</Typography>
													</div>
												</div>
											}
											className="border-bottom"
											style={{
												paddingBottom: 12,
												background:
													"rgba(0, 0, 0, 0.02)",
											}}
										/>
										<Box
											className="flex-grow-1 overflow-auto"
											style={{ maxHeight: 515 }}
										>
											<List className="p-3">
												<AnimatePresence>
													{terms.map(
														(term, index) => {
															const isSelected =
																selectedTerm?.id ===
																term.id;
															const accepted =
																checkedTerms.has(
																	term.id,
																);

															return (
																<motion.div
																	key={
																		term.id
																	}
																	initial={{
																		opacity: 0,
																		x: -20,
																	}}
																	animate={{
																		opacity: 1,
																		x: 0,
																	}}
																	transition={{
																		delay:
																			index *
																			0.1,
																	}}
																	whileHover={{
																		scale: 1.02,
																	}}
																>
																	<ListItem
																		disablePadding
																		className={`mb-2 rounded-3 ${isSelected ? "bg-primary bg-opacity-10 border border-primary border-opacity-50" : "bg-light border-0"}`}
																	>
																		<ListItemButton
																			onClick={() =>
																				setSelectedTerm(
																					term,
																				)
																			}
																			className="p-3"
																		>
																			<ListItemIcon
																				className="me-2"
																				style={{
																					minWidth: 40,
																				}}
																			>
																				<motion.div
																					className={`p-2 rounded-2 ${accepted ? "bg-success bg-opacity-10" : isSelected ? "bg-primary bg-opacity-25" : "bg-light"}`}
																					whileHover={{
																						scale: 1.1,
																					}}
																				>
																					{accepted ? (
																						<motion.div
																							initial={{
																								scale: 0,
																							}}
																							animate={{
																								scale: 1,
																							}}
																							transition={{
																								type: "spring",
																								stiffness: 500,
																							}}
																						>
																							<MyCheckIcon
																								style={{
																									fontSize: 16,
																									color: "#4caf50",
																								}}
																							/>
																						</motion.div>
																					) : (
																						<MyArticleOutlinedIcon
																							style={{
																								fontSize: 16,
																								color: isSelected
																									? "#fff"
																									: "#666",
																							}}
																						/>
																					)}
																				</motion.div>
																			</ListItemIcon>
																			<ListItemText
																				primary={
																					<Box
																						display="flex"
																						alignItems="center"
																						gap={
																							1
																						}
																					>
																						<Typography
																							variant="body1"
																							className="text-capitalize tnc-doc-list-text"
																							sx={{
																								fontWeight:
																									isSelected
																										? 600
																										: 400,
																								fontSize:
																									"1rem",
																							}}
																						>
																							{
																								term.title
																							}
																						</Typography>
																						{term.isRequired && (
																							<Chip
																								label="Required"
																								size="small"
																								className="mt-1 bg-danger bg-opacity-10 text-danger border border-danger border-opacity-50"
																								sx={{
																									height: 20,
																									fontWeight: 600,
																								}}
																							/>
																						)}
																					</Box>
																				}
																				secondary={
																					null
																				}
																			/>
																		</ListItemButton>
																	</ListItem>
																</motion.div>
															);
														},
													)}
												</AnimatePresence>
											</List>
										</Box>
									</Card>
								</motion.div>
							</Grid>

							{/* Right Panel - 75% */}
							<Grid item xs={12} lg={8}>
								<motion.div
									initial={{ opacity: 0, x: 30 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.5, delay: 0.4 }}
									className="h-100"
								>
									<AnimatePresence mode="wait">
										{selectedTerm ? (
											<motion.div
												key={selectedTerm.id}
												initial={{ opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: -10 }}
												transition={{ duration: 0.3 }}
												style={{ height: "100%" }}
											>
												<Card
													className="bg-white bg-opacity-95 border shadow-sm overflow-hidden d-flex flex-column min-vh-50"
													sx={{
														minHeight: 515,
														maxHeight: 515,
														height: 515,
														display: 'flex',
														flexDirection: 'column',
													}}
												>
													{/* Equal height for document and content cards */}
													<CardHeader
														title={
															<Typography
																variant="h5"
																className="fw-bold"
															>
																{
																	selectedTerm.title
																}
															</Typography>
														}
														subheader={
															<Typography
																variant="body2"
																className="text-gray-600"
															>
																Updated{" "}
																{new Date(
																	selectedTerm.updatedAt,
																).toLocaleDateString(
																	"en-US",
																	{
																		year: "numeric",
																		month: "short",
																		day: "numeric",
																	},
																)}
															</Typography>
														}
														action={
															selectedTerm.isRequired && (
																<Chip
																	label="Required"
																	size="small"
																	className="bg-danger bg-opacity-10 text-danger border border-danger border-opacity-50"
																	sx={{
																		height: 22,
																		fontWeight: 500,
																	}}
																/>
															)
														}
														className="border-bottom pb-3 bg-light"
													/>
													<CardContent className="p-0 d-flex flex-column flex-grow-1 overflow-hidden">
														{/* Content */}
														<Box
															className="p-4 flex-grow-1 overflow-auto tnc-doc-content-text"
															style={{
																minHeight: 350,
																maxHeight: 350,
																fontSize:
																	"1.05rem",
															}}
															dangerouslySetInnerHTML={{
																__html: selectedTerm.content,
															}}
														/>
														{/* Checkbox */}
														<Box className="p-4 border-top bg-light">
															<motion.div
																className={`px-4 py-2 rounded-3 ${checkedTerms.has(selectedTerm.id) ? "bg-success bg-opacity-10 border border-success border-opacity-25" : "bg-white border border-light"}`}
																style={{
																	cursor: "pointer",
																}}
																onClick={() =>
																	handleTermToggle(
																		selectedTerm.id,
																	)
																}
																whileHover={{
																	scale: 1.01,
																}}
																whileTap={{
																	scale: 0.99,
																}}
															>
																<FormControlLabel
																	control={
																		<Checkbox
																			checked={checkedTerms.has(
																				selectedTerm.id,
																			)}
																			onChange={() =>
																				handleTermToggle(
																					selectedTerm.id,
																				)
																			}
																			className={
																				checkedTerms.has(
																					selectedTerm.id,
																				)
																					? "text-success"
																					: ""
																			}
																		/>
																	}
																	label={
																		<Box className="tnc-accept-checkbox-label">
																			<Typography
																				variant="h6"
																				className="fw-medium"
																			>
																				I
																				have
																				read
																				and
																				agree
																				to
																				the{" "}
																				{
																					selectedTerm.title
																				}
																			</Typography>
																			<Typography
																				variant="body1"
																				className="text-gray-600"
																			>
																				Click
																				to
																				acknowledge
																				your
																				agreement
																			</Typography>
																		</Box>
																	}
																/>
															</motion.div>
														</Box>
													</CardContent>
												</Card>
											</motion.div>
										) : (
											<Card
												className="d-flex align-items-center justify-content-center bg-white bg-opacity-95"
												style={{ height: 515 }}
											>
												<Box className="text-center">
													<MyArticleOutlinedIcon
														className="mb-3"
														style={{
															fontSize: 64,
															color: "#999",
														}}
													/>
													<Typography
														variant="h5"
														className="text-gray-600"
													>
														Select a document to
														view
													</Typography>
												</Box>
											</Card>
										)}
									</AnimatePresence>
								</motion.div>
							</Grid>
						</Grid>

						{/* Footer Action Bar */}
						<motion.div
							className="mt-4"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.5 }}
						>
							<Card className="bg-white bg-opacity-95 border shadow-sm">
								<CardContent className="p-3">
									<div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
										<div className="d-flex align-items-center mb-3 mb-sm-0">
											<motion.div
												className="d-flex align-items-center justify-content-center me-3 bg-primary bg-opacity-10 rounded-2"
												style={{
													height: 40,
													width: 40,
												}}
												animate={{
													rotate: [0, 5, -5, 0],
												}}
												transition={{
													duration: 4,
													repeat: Infinity,
													ease: "easeInOut",
												}}
											>
												<MyShieldIcon
													style={{ fontSize: 20 }}
												/>
											</motion.div>
											<div>
												<Typography
													variant="h6"
													className="fw-medium tnc-doc-count-text"
												>
													{checkedTerms.size} of{" "}
													{terms.length} documents
													accepted
												</Typography>
												<Typography
													variant="body1"
													className="text-gray-600"
												>
													{allRequiredChecked
														? "All required accepted"
														: `${requiredTerms.length - checkedRequiredCount} required remaining`}
												</Typography>
											</div>
										</div>
										<div className="d-flex gap-2">
											<Button
												variant="outlined"
												color="error"
												className="btn-decline-custom text-white"
												onClick={handleDecline}
												startIcon={
													<MyCloseIcon
														style={{
															color: "inherit",
														}}
													/>
												}
												size="large"
											>
												Decline
											</Button>
											<motion.div
												whileHover={{
													scale: allRequiredChecked
														? 1.02
														: 1,
												}}
												whileTap={{
													scale: allRequiredChecked
														? 0.98
														: 1,
												}}
											>
												<Button
													variant="contained"
													color="primary"
													onClick={handleAccept}
													disabled={
														!allRequiredChecked ||
														submitting
													}
													startIcon={
														submitting ? null : (
															<MyCheckIcon
																style={{
																	color: "inherit",
																}}
															/>
														)
													}
													size="large"
													sx={
														allRequiredChecked
															? {
																	background:
																		"var(--bs-primary)",
																	color: "#fff",
																	"&:hover": {
																		background:
																			"var(--bs-primary)",
																	},
																}
															: {}
													}
												>
													{submitting ? (
														<>
															<CircularProgress
																size={20}
																className="me-2"
															/>
															Processing...
														</>
													) : (
														"Accept"
													)}
												</Button>
											</motion.div>
										</div>
									</div>
								</CardContent>
							</Card>
						</motion.div>
					</Container>
				</main>

				{/* Footer */}
				<motion.footer
					className="border-top mt-auto bg-white bg-opacity-75"
					style={{ backdropFilter: "blur(20px)", fontWeight: 500 }}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.6 }}
				>
					<FooterComponent />
				</motion.footer>
			</div>

			{/* Snackbar */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default TnCSection;
